import clsx from 'clsx'
import { useInView } from 'framer-motion'
import { useRef, type ComponentProps } from 'react'
import { IconButton } from 'ui/src/components/Button/IconButton'
import { ArrowDownIcon } from 'ui/src/icons/ArrowIcon'
import { type VideoRef } from '@/components/Video/Video'
import { ImageStory, VideoStory } from './Story'
import type { Item, VideoItem, ImageItem } from './StoryCarousel.types'
import { storiesContainer, stories, scrollButton, DEFAULT_SCROLL_AMOUNT } from './StoryCarouselDesktop.css'

type StoryCarouselDesktopProps = {
  items: Array<Item>
}

export function StoryCarouselDesktop({ items }: StoryCarouselDesktopProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const firstItemRef = useRef<HTMLDivElement>(null)
  const lastItemRef = useRef<HTMLDivElement>(null)
  const isFirstItemVisible = useInView(firstItemRef, { root: scrollContainerRef, amount: 'all' })
  const isLastItemVisible = useInView(lastItemRef, { root: scrollContainerRef, amount: 'all' })

  if (items.length === 0) return null

  const scrollLeft = () => scrollContainerRef.current?.scrollBy({ left: -DEFAULT_SCROLL_AMOUNT, behavior: 'smooth' })
  const scrollRight = () => scrollContainerRef.current?.scrollBy({ left: DEFAULT_SCROLL_AMOUNT, behavior: 'smooth' })

  const firstItem = items[0]
  const lastItem = items[items.length - 1]

  return (
    <div className={storiesContainer}>
      {!isFirstItemVisible && <ScrollButton direction="left" onClick={scrollLeft} />}

      <div ref={scrollContainerRef} className={stories}>
        <div id="first-item" ref={firstItemRef}>
          <Story item={firstItem} />
        </div>
        {items.slice(1, items.length - 1).map((item) => (
          <Story key={item.id} item={item} />
        ))}
        <div id="last-item" ref={lastItemRef}>
          <Story item={lastItem} />
        </div>
      </div>

      {!isLastItemVisible && <ScrollButton direction="right" onClick={scrollRight} />}
    </div>
  )
}

function Story({ item }: { item: Item }) {
  switch (item.asset.type) {
    case 'image':
      return <ImageStory item={item as ImageItem} />
    case 'video':
      return <PausableVideoStory {...(item as VideoItem)} />
    default:
      return null
  }
}

function PausableVideoStory(item: VideoItem) {
  const videoRef = useRef<VideoRef>(null)

  return (
    <div onMouseEnter={() => videoRef.current?.play()} onMouseLeave={() => videoRef.current?.reset()}>
      <VideoStory ref={videoRef} item={item} />
    </div>
  )
}

function ScrollButton({
  direction,
  className,
  ...others
}: ComponentProps<typeof IconButton> & { direction: 'left' | 'right' }) {
  return (
    <IconButton
      className={clsx(scrollButton, className)}
      {...others}
      variant="secondary-alt"
      data-direction={direction}
    >
      <ArrowDownIcon size="1.5rem" />
    </IconButton>
  )
}
